/**
 * Attachments on the client (SPEC §13.1): images are resized to max 2000 px WebP (q 0.8)
 * with a 400 px WebP thumbnail (re-encoding strips EXIF); PDFs pass through (≤ 25 MB).
 * The attachment row appears immediately with a local preview; uploads queue offline.
 */
import { useEffect, useState } from 'react'
import { ancestorsOf, defaultAttachmentTag, resolveLabel, shortId, type Attachment } from '@terrazzo/core'
import { actions } from '../store/actions'
import { store } from '../store/store'
import { myId } from '../store/me'
import { showToast } from '../store/toasts'
import { enqueue, localBlob, processQueue } from './uploadQueue'

const MAX_PDF = 25 * 1024 * 1024
const EXT: Record<string, string> = {
  'image/webp': 'webp',
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/gif': 'gif',
  'application/pdf': 'pdf',
  'text/plain': 'txt',
  'video/mp4': 'mp4',
  'video/quicktime': 'mov',
}
/** Uploaded as-is (≤ 25 MB); must match the Worker's allowlist and key extensions (api/files.ts). */
const PASS_THROUGH = new Set(['application/pdf', 'text/plain', 'video/mp4', 'video/quicktime'])
/** `accept` for pickers that take anything attachFiles can store. */
export const ATTACHABLE = 'image/*,' + [...PASS_THROUGH].join(',')

async function encode(bmp: ImageBitmap, maxSide: number, quality: number): Promise<{ blob: Blob; w: number; h: number }> {
  const scale = Math.min(1, maxSide / Math.max(bmp.width, bmp.height))
  const w = Math.max(1, Math.round(bmp.width * scale))
  const h = Math.max(1, Math.round(bmp.height * scale))
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')!
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(bmp, 0, 0, w, h)
  const toBlob = (type: string, q: number) => new Promise<Blob | null>((res) => canvas.toBlob(res, type, q))
  let blob = await toBlob('image/webp', quality)
  // Safari may not encode WebP; fall back to JPEG.
  if (!blob || blob.type !== 'image/webp') blob = await toBlob('image/jpeg', 0.85)
  if (!blob) throw new Error('Could not encode image')
  return { blob, w, h }
}

/** `maxSide`: the long side of the full copy (2000 px for an item's photo). */
export async function processImage(file: File, maxSide = 2000) {
  const bmp = await createImageBitmap(file, { imageOrientation: 'from-image' })
  try {
    const full = await encode(bmp, maxSide, 0.8)
    const thumb = await encode(bmp, 400, 0.75)
    return { full, thumb }
  } finally {
    bmp.close()
  }
}

function defaultTagFor(itemId: string): string | undefined {
  const it = store.get(itemId)
  const anc = ancestorsOf(store.tree, itemId)
    .map((a) => store.get(a)!)
    .filter(Boolean)
  return defaultAttachmentTag(resolveLabel('attachments', it, anc, ''))
}

/** Attach files to an item; resolves with the attachments that were added (in order). */
export async function attachFiles(itemId: string, files: File[], opts: { tags?: string[] } = {}): Promise<Attachment[]> {
  const tag = defaultTagFor(itemId)
  const added: Attachment[] = []
  for (const file of files) {
    try {
      const attId = shortId()
      const isImage = file.type.startsWith('image/')
      if (!isImage && !PASS_THROUGH.has(file.type)) {
        showToast(`${file.name}: only images, PDFs, text files and MP4/MOV videos can be attached`)
        continue
      }
      let att: Attachment
      if (isImage) {
        const { full, thumb } = await processImage(file)
        const key = `att/${itemId}/${attId}.${EXT[full.blob.type] ?? 'jpg'}`
        const thumbKey = `att/${itemId}/${attId}.thumb.webp`
        await enqueue({ key, itemId, attId, variant: 'full', mime: full.blob.type }, full.blob)
        await enqueue({ key: thumbKey, itemId, attId, variant: 'thumb', mime: thumb.blob.type }, thumb.blob)
        att = {
          id: attId,
          key,
          thumbKey,
          mime: full.blob.type,
          bytes: full.blob.size,
          w: full.w,
          h: full.h,
          tags: opts.tags ?? (tag ? [tag] : []),
          name: file.name.replace(/\.[^.]+$/, '') + '.' + (EXT[full.blob.type] ?? 'jpg'),
          addedBy: myId(),
          addedAt: Date.now(),
        }
      } else {
        if (file.size > MAX_PDF) {
          showToast(`${file.name} is larger than 25 MB`)
          continue
        }
        const key = `att/${itemId}/${attId}.${EXT[file.type]}`
        await enqueue({ key, itemId, attId, variant: 'full', mime: file.type }, file)
        att = {
          id: attId,
          key,
          mime: file.type,
          bytes: file.size,
          tags: opts.tags ?? (tag ? [tag] : []),
          name: file.name,
          addedBy: myId(),
          addedAt: Date.now(),
        }
      }
      actions.putAttachment(itemId, att)
      added.push(att)
    } catch (e) {
      console.error(e)
      showToast(`Couldn't attach ${file.name}`)
    }
  }
  void kickUploads()
  return added
}

let timer: ReturnType<typeof setTimeout> | null = null
export async function kickUploads(): Promise<void> {
  if (!navigator.onLine) return
  const remaining = await processQueue()
  if (remaining && !timer) {
    timer = setTimeout(() => {
      timer = null
      void kickUploads()
    }, 30_000)
  }
  if (remaining) {
    try {
      const reg = await navigator.serviceWorker?.ready
      await (reg as ServiceWorkerRegistration & { sync?: { register(tag: string): Promise<void> } })?.sync?.register('uploads')
    } catch {
      /* Background Sync unsupported (iOS) */
    }
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => void kickUploads())
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void kickUploads()
  })
}

/**
 * URL for an attachment: local blob while pending/offline, else the Worker file route.
 * `localFirst` returns undefined until the local lookup is done, so a just-added file doesn't
 * first request /f/ (a 404 until its upload lands).
 */
export function useFileUrl(key: string | undefined, opts: { localFirst?: boolean } = {}): string | undefined {
  const [url, setUrl] = useState<string | undefined>(key && !opts.localFirst ? `/f/${key}` : undefined)
  useEffect(() => {
    if (!key) return
    let revoke: string | null = null
    let alive = true
    void localBlob(key).then(
      (b) => {
        if (!alive) return
        if (b) {
          revoke = URL.createObjectURL(b)
          setUrl(revoke)
        } else setUrl(`/f/${key}`)
      },
      () => alive && setUrl(`/f/${key}`),
    )
    return () => {
      alive = false
      if (revoke) URL.revokeObjectURL(revoke)
    }
  }, [key])
  return url
}
